import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarRange, CreditCard, FileText, Loader2, PauseCircle, PlayCircle, Search, Send } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Input } from '../ui/input';
import { EtatHistorique } from '../invitation/EtatHistorique';
import { fmtEur, fmtDate, openGedDoc } from './gedUtils';
import { ReportEcheancesDialog } from './ReportEcheancesDialog';

const API = process.env.REACT_APP_BACKEND_URL;
const PRODUITS = [['', 'Tous'], ['rcds', 'RCDS BATICO Annuel'], ['archos', 'ARCHOS'], ['nominative', 'Nominative'], ['avenant', 'Avenant'], ['domium', 'DOS-DOMIUM']];
const ETAT_CLS = { partiel_valide: 'bg-emerald-100 text-emerald-800', acquitte: 'bg-emerald-600 text-white', impaye: 'bg-orange-100 text-orange-800', resilie: 'bg-red-600 text-white' };
const ECH_CLS = (e) => (e.statut === 'paye' ? 'bg-emerald-500' : (e.date_echeance || '9999') < new Date().toISOString().slice(0, 10) ? 'bg-red-500' : 'bg-slate-300');

const DateEditable = ({ r, i, e, headers, onReload }) => {
  const [edit, setEdit] = useState(false);
  const [val, setVal] = useState((e.date_echeance || '').slice(0, 10));
  const save = async () => {
    try {
      await axios.put(`${API}/api/paiements-cb/admin/${r.source}/${r.id}/echeances/${i}`, { date_echeance: val }, { headers });
      toast.success('Date modifiée — le membre a reçu son nouvel échéancier');
      setEdit(false);
      onReload();
    } catch (err) { toast.error(err.response?.data?.detail || 'Modification impossible'); }
  };
  if (!edit) {
    return <button type="button" onClick={() => setEdit(true)} className="underline decoration-dotted hover:text-sky-700" data-testid={`cb-date-edit-${r.source}-${r.id}-${i}`}>{fmtDate(e.date_echeance)}</button>;
  }
  return (
    <span className="inline-flex items-center gap-1">
      <input type="date" value={val} min={new Date().toISOString().slice(0, 10)} onChange={(ev) => setVal(ev.target.value)} className="h-6 rounded border border-slate-300 px-1 text-[11px]" data-testid={`cb-date-input-${r.source}-${r.id}-${i}`} />
      <button type="button" onClick={save} className="text-emerald-700 font-semibold" data-testid={`cb-date-save-${r.source}-${r.id}-${i}`}>OK</button>
      <button type="button" onClick={() => setEdit(false)} className="text-slate-500">✕</button>
    </span>
  );
};

const Ligne = ({ r, headers, onReload, superAdmin }) => {
  const [busy, setBusy] = useState(null);
  const [report, setReport] = useState(false);
  const tid = `${r.source}-${r.id}`;
  const peutReporter = superAdmin && r.source !== 'ess' && r.echeances.some((e) => e.statut !== 'paye');
  const toggle = async () => {
    setBusy('pa');
    try {
      await axios.post(`${API}/api/paiements-cb/admin/${r.source}/${r.id}/prelevement`, { actif: !r.prelevement_auto.actif }, { headers });
      toast.success(r.prelevement_auto.actif ? 'Prélèvement suspendu — le membre est prévenu' : 'Prélèvement réactivé — le membre est prévenu');
      onReload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action impossible'); } finally { setBusy(null); }
  };
  const relancer = async () => {
    setBusy('rel');
    try {
      const res = await axios.post(`${API}/api/impayes/${r.source === 'invitation' ? 'rcds' : 'domium'}/${r.id}/relance`, { origin: window.location.origin }, { headers });
      toast.success(`Relance envoyée à ${res.data.email}`);
      onReload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Relance impossible'); } finally { setBusy(null); }
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-2" data-testid={`cb-row-${tid}`}>
      <div className="flex flex-wrap items-center gap-2">
        <CreditCard className="h-4 w-4 text-slate-500" />
        <span className="font-semibold text-slate-900">{r.nom || '—'}</span>
        <span className="text-xs text-slate-500">{r.email}</span>
        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{r.produit_label}</span>
        <span className="text-[10px] text-slate-500">{r.reference} · {r.mode === '4x' ? 'CB en 4 fois' : 'CB en 1 fois'}</span>
        {r.etat_label && <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${ETAT_CLS[r.etat]}`} data-testid={`cb-etat-${tid}`}>{r.etat_label}</span>}
        <span className="ml-auto text-sm tabular-nums" data-testid={`cb-montants-${tid}`}>
          <strong className="text-emerald-700">{fmtEur(r.paye)}</strong> / {fmtEur(r.total)}{r.reste > 0 && <span className="text-slate-500"> · reste {fmtEur(r.reste)}</span>}
        </span>
      </div>
      {r.echeances.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid={`cb-echeances-${tid}`}>
          {r.echeances.map((e, i) => (
            <span key={i} className="inline-flex items-center gap-1.5 text-[11px] rounded-lg border border-slate-200 px-2 py-1" title={e.tentatives ? `${e.tentatives} tentative(s) de prélèvement` : ''}>
              <span className={`h-2 w-2 rounded-full ${ECH_CLS(e)}`} /> {i + 1}/4 · {fmtEur(e.montant)} · {superAdmin && e.statut !== 'paye' && r.source !== 'ess'
                ? <DateEditable r={r} i={i} e={e} headers={headers} onReload={onReload} /> : fmtDate(e.date_echeance)}
              {e.statut === 'paye' ? <span className="text-emerald-700">payée {fmtDate(e.date_paiement)}</span> : e.prelevement_abandonne ? <span className="text-red-600">prélèvement refusé</span> : null}
            </span>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {r.paiements.map((p, i) => (
          <button key={i} type="button" disabled={!p.ged_id} onClick={() => openGedDoc(p.ged_id, headers)} data-testid={`cb-recu-${tid}-${i}`}
            className="inline-flex items-center gap-1 text-[11px] rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-emerald-800 disabled:opacity-50">
            <FileText className="h-3 w-3" /> {p.recu_numero || 'Reçu'} · {fmtEur(p.montant)} · {fmtDate(p.date)}{p.auto ? ' · auto' : ''}
          </button>
        ))}
        {r.prelevement_auto.existe && (
          <button type="button" onClick={toggle} disabled={!!busy} data-testid={`cb-prelevement-toggle-${tid}`}
            className={`inline-flex items-center gap-1 text-[11px] rounded-md border px-2 py-1 ${r.prelevement_auto.actif ? 'border-violet-200 bg-violet-50 text-violet-800' : 'border-slate-300 bg-slate-50 text-slate-700'}`}>
            {busy === 'pa' ? <Loader2 className="h-3 w-3 animate-spin" /> : r.prelevement_auto.actif ? <PauseCircle className="h-3 w-3" /> : <PlayCircle className="h-3 w-3" />}
            {r.prelevement_auto.actif ? `Prélèvement actif (${r.prelevement_auto.carte || 'carte'}) — Suspendre` : 'Prélèvement suspendu — Réactiver'}
          </button>
        )}
        {peutReporter && (
          <button type="button" onClick={() => setReport(true)} data-testid={`cb-report-${tid}`}
            className="inline-flex items-center gap-1 text-[11px] rounded-md border border-sky-200 bg-sky-50 px-2 py-1 text-sky-800 hover:bg-sky-100">
            <CalendarRange className="h-3 w-3" /> Reporter les échéances
          </button>
        )}
        {['impaye', 'resilie'].includes(r.etat) && (
          <button type="button" onClick={relancer} disabled={!!busy} data-testid={`cb-relance-${tid}`}
            className="inline-flex items-center gap-1 text-[11px] rounded-md border border-red-200 bg-red-50 px-2 py-1 text-red-700">
            {busy === 'rel' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />} Relancer
          </button>
        )}
        <span className="ml-auto"><EtatHistorique items={r.historique} testId={`cb-historique-${tid}`} /></span>
      </div>
      {report && <ReportEcheancesDialog r={r} headers={headers} onClose={() => setReport(false)} onDone={() => { setReport(false); onReload(); }} />}
    </div>
  );
};

export const EcheanciersCbPanel = () => {
  const { getAuthHeader, user } = useAuth();
  const [produit, setProduit] = useState('');
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    axios.get(`${API}/api/paiements-cb/admin/echeanciers`, { headers: getAuthHeader(), params: { produit, q } })
      .then((r) => setData(r.data)).catch(() => setData({ items: [], error: true }));
  }, [produit, q]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  return (
    <div className="space-y-3" data-testid="echeanciers-cb-panel">
      <div className="flex flex-wrap items-center gap-2">
        {PRODUITS.map(([k, l]) => (
          <button key={k || 'tous'} type="button" onClick={() => setProduit(k)} data-testid={`cb-filtre-${k || 'tous'}`}
            className={`text-xs px-3 py-1.5 rounded-full border ${produit === k ? 'bg-[#0a3d62] text-white border-[#0a3d62]' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>{l}</button>
        ))}
        <div className="relative ml-auto">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Membre, e-mail, référence" className="h-8 w-60 pl-8 text-sm" data-testid="cb-search-input" />
        </div>
      </div>
      {data && !data.error && (
        <p className="text-sm text-slate-600" data-testid="cb-synthese">
          {data.items.length} contrat(s) CB · encaissé <strong>{fmtEur(data.total_encaisse)}</strong> · reste dû <strong>{fmtEur(data.total_reste)}</strong> · {data.nb_prelevement_actif} prélèvement(s) automatique(s) actif(s)
        </p>
      )}
      {!data ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : data.items.length === 0
        ? <p className="text-sm text-slate-500 py-6 text-center" data-testid="cb-empty">Aucun paiement CB pour ce filtre.</p>
        : data.items.map((r) => <Ligne key={`${r.source}-${r.id}`} r={r} headers={getAuthHeader()} onReload={load} superAdmin={user?.role === 'super_admin'} />)}
    </div>
  );
};
